import { supabase } from "@/integrations/supabase/client";

// Tenant-aware conversation functions using the new database functions
export async function getTenantConversationsWithDetails(
  options: { limit?: number; offset?: number; schema?: string; filterAssigned?: string } = {}
) {
  try {
    const schema = options.schema || (await supabase.rpc('get_user_tenant_schema')).data;
    if (!schema) throw new Error('No tenant schema found');

    // Use defaults: 50 for pagination, 0 offset, 'all' for filter
    const { limit = 50, offset = 0, filterAssigned = 'all' } = options;

    const params: Record<string, unknown> = {
      p_tenant_schema: schema,
      p_limit: limit,
      p_offset: offset,
      p_filter_assigned: filterAssigned
    };

    const { data, error } = await supabase.rpc('get_tenant_conversations_with_details', params);

    // V1407: Diagnostic logging - log raw RPC response BEFORE filtering
    // PHASE 1A: Added inbox_category debugging
    console.log('[V1407] 📥 Raw RPC response (before filtering):', {
      dataCount: data?.length,
      firstConvEscalation: data?.[0]?.escalation_status,
      firstConvAgentMode: data?.[0]?.agent_mode,
      firstConvInboxCategory: data?.[0]?.inbox_category,
      firstConvId: data?.[0]?.id?.substring(0, 8),
      rawFirstConv: data?.[0] ? {
        id: data[0].id?.substring(0, 8),
        escalation_status: data[0].escalation_status,
        agent_mode: data[0].agent_mode,
        inbox_category: data[0].inbox_category,
        platform_metadata: data[0].platform_metadata ? 'present' : 'missing'
      } : null
    });

    // 🧪 [TEST MODE FILTER] Exclude test conversations from tenant UI
    // Test conversations are marked with is_test_conversation = true
    // This ensures tenant users never see test data in their conversation list
    const filteredData = data?.filter((conv: Record<string, unknown>) => conv.is_test_conversation !== true) || null;

    // Extract total_count from first row (window function returns same value for all rows)
    const totalCount = data && data.length > 0 ? data[0].total_count : 0;

    // V1407: Diagnostic logging - log AFTER filtering to see if filter changed escalation_status
    console.log('[V1407] 📤 Filtered response (after test conversation filter):', {
      dataCount: filteredData?.length,
      totalCount,
      firstConvEscalation: filteredData?.[0]?.escalation_status,
      firstConvAgentMode: filteredData?.[0]?.agent_mode,
      firstConvId: filteredData?.[0]?.id?.substring(0, 8),
      didFilterChangeEscalation: data?.[0]?.escalation_status !== filteredData?.[0]?.escalation_status
    });

    return { data: filteredData, totalCount, error };
  } catch (error) {
    return { data: null, totalCount: 0, error: error as Error };
  }
}

// Customer-centric conversations: One row per customer with latest conversation data
export async function getTenantCustomersWithConversations(
  options: {
    schema?: string;
    limit?: number;
    offset?: number;
    filterAssigned?: string;
    search?: string;
    inboxView?: string;
    statusFilter?: string;
    aiModeFilter?: string;
  } = {}
) {
  try {
    const schema = options.schema || (await supabase.rpc('get_user_tenant_schema')).data;
    if (!schema) throw new Error('No tenant schema found');

    const {
      limit = 50, offset = 0, filterAssigned = 'all',
      search = null, inboxView = 'active', statusFilter = 'all', aiModeFilter = 'all'
    } = options;

    const filterParams = {
      p_tenant_schema: schema,
      p_filter_assigned: filterAssigned,
      p_search: search,
      p_inbox_view: inboxView,
      p_status_filter: statusFilter,
      p_ai_mode_filter: aiModeFilter
    };

    // Fire data + count RPCs in parallel for maximum speed
    const [dataResult, countResult] = await Promise.all([
      supabase.rpc('get_tenant_customers_with_conversations', {
        ...filterParams,
        p_limit: limit,
        p_offset: offset
      }),
      supabase.rpc('get_tenant_customer_count', filterParams)
    ]);

    if (dataResult.error) {
      return { data: null, totalCount: 0, error: dataResult.error };
    }

    const totalCount = typeof countResult.data === 'number' ? countResult.data : 0;

    return { data: dataResult.data, totalCount, error: null };
  } catch (error) {
    return { data: null, totalCount: 0, error: error as Error };
  }
}

// Customer-centric timeline: Messages from ALL conversations for a customer
export async function getTenantCustomerMessagesTimeline(
  customerId: string,
  options: {
    schema?: string;
    limit?: number;
    cursorCreatedAt?: string | null;
    cursorId?: string | null;
  } = {}
) {
  try {
    const schema = options.schema || (await supabase.rpc('get_user_tenant_schema')).data;
    if (!schema) throw new Error('No tenant schema found');

    const { limit = 50, cursorCreatedAt = null, cursorId = null } = options;

    const { data, error } = await supabase.rpc('get_tenant_customer_messages_timeline', {
      p_tenant_schema: schema,
      p_customer_id: customerId,
      p_limit: limit,
      p_cursor_created_at: cursorCreatedAt,
      p_cursor_id: cursorId
    });

    if (error) return { data: null, error };

    // Filter test messages (same pattern as getTenantConversationMessages)
    const filteredData = (data || []).filter((msg: Record<string, unknown>) => !msg.is_test_message);

    return { data: filteredData, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

// Deep-link resolution: conversation ID → customer ID
export async function getCustomerIdForConversation(
  conversationId: string,
  schema?: string
) {
  try {
    const resolvedSchema = schema || (await supabase.rpc('get_user_tenant_schema')).data;
    if (!resolvedSchema) throw new Error('No tenant schema found');

    const { data, error } = await supabase.rpc('get_customer_id_for_conversation', {
      p_tenant_schema: resolvedSchema,
      p_conversation_id: conversationId
    });

    if (error) return { data: null, error };
    return { data: data as string, error: null };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

/**
 * Derives an outgoing message's display status from the raw row returned by
 * `get_tenant_messages_direct`.
 *
 * `delivery_status` is checked FIRST, before `read_at`/`delivered_at`, and is
 * the only source that can ever produce 'failed' here: a message Meta rejects
 * never gets a `read_at` or `delivered_at` (see the 2026-08-21 fix to
 * `public.save_tenant_message_status_update`), so a status chain built only
 * from those two columns can never distinguish "rejected" from "still in
 * flight" — it silently reports the safer-looking but wrong 'sending'/'sent'.
 *
 * NOTE (2026-08-21): this function backs getTenantConversationMessages below,
 * which has zero callers anywhere in src/ today (verified by
 * `grep -rn getTenantConversationMessages src/`) — the live conversation
 * screens read through `get_tenant_conversation_messages_v2` /
 * `get_tenant_conversation_messages_with_status` instead, and already derive
 * 'failed' correctly through unrelated mechanisms. This is dead-path
 * hardening, not a fix to what a user sees today: it exists so that if this
 * function is ever wired back in, or copied as a starting point for new code,
 * it does not resurrect the "rejected looks delivered" bug.
 */
export function deriveOutgoingMessageStatus(msg: {
  external_id?: unknown;
  delivery_status?: unknown;
  read_at?: unknown;
  delivered_at?: unknown;
}): 'sending' | 'sent' | 'delivered' | 'read' | 'failed' {
  if (!msg.external_id) return 'sending';
  if (msg.delivery_status === 'failed') return 'failed';
  if (msg.read_at) return 'read';
  if (msg.delivered_at) return 'delivered';
  return 'sent';
}

export async function getTenantConversationMessages(conversationId: string) {
  try {
    const { data: schema } = await supabase.rpc('get_user_tenant_schema');
    if (!schema) throw new Error('No tenant schema found');

    console.log(`[getTenantConversationMessages] 🔍 Fetching messages for conversation ${conversationId} in schema ${schema}`);

    // Use the new direct query function instead of execute_sql
    const { data, error } = await supabase.rpc('get_tenant_messages_direct', {
      tenant_schema: schema,
      conversation_id_param: conversationId,
      limit_count: 100
    });

    if (error) {
      console.error(`[getTenantConversationMessages] ❌ Error fetching messages:`, error);
      return { data: null, error };
    }

    console.log(`[getTenantConversationMessages] ✅ Fetched ${data?.length || 0} messages`);

    // 🧪 [TEST MODE FILTER] Exclude test messages from tenant UI
    // Test messages are marked with is_test_message = true
    // This ensures tenant users never see test data in their message list
    const filteredData = (data || []).filter((msg: Record<string, unknown>) => !msg.is_test_message);

    // Process the messages to calculate proper status
    const processedMessages = filteredData.map((msg: Record<string, unknown>) => {
      const isOutgoing = msg.sender_type === 'user' || msg.sender_type === 'agent';

      // Incoming messages are always delivered; outgoing messages derive
      // their status from delivery_status first (see deriveOutgoingMessageStatus
      // above for why that order matters), falling back to read_at/delivered_at.
      const calculatedStatus = isOutgoing ? deriveOutgoingMessageStatus(msg) : 'delivered';

      return {
        ...msg,
        status: calculatedStatus
      };
    });

    console.log(`[getTenantConversationMessages] 📊 Sample message statuses:`, 
      processedMessages.slice(0, 3).map((m: Record<string, unknown>) => ({
        id: m.id,
        content: m.content?.substring(0, 20),
        external_id: m.external_id,
        delivered_at: m.delivered_at,
        read_at: m.read_at,
        status: m.status
      }))
    );

    return { data: processedMessages, error: null };
  } catch (error) {
    console.error(`[getTenantConversationMessages] ❌ Exception:`, error);
    return { data: null, error: error as Error };
  }
}

export async function createTenantCustomer(whatsappId: string, phoneNumber: string, name?: string) {
  try {
    const { data: schema } = await supabase.rpc('get_user_tenant_schema');
    if (!schema) throw new Error('No tenant schema found');

    const { data, error } = await supabase.rpc('find_or_create_tenant_customer', {
      tenant_schema: schema,
      external_id_param: whatsappId,
      phone_number_param: phoneNumber,
      name_param: name || null
    });

    return { data, error };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

export async function createTenantConversation(customerId: string) {
  try {
    const { data: schema } = await supabase.rpc('get_user_tenant_schema');
    if (!schema) throw new Error('No tenant schema found');

    const { data, error } = await supabase.rpc('find_or_create_tenant_conversation', {
      tenant_schema: schema,
      customer_id_param: customerId
    });

    return { data, error };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

export async function saveTenantMessage(
  conversationId: string,
  senderType: string,
  senderId: string,
  senderUuid?: string,
  content: string,
  messageType: string = 'text',
  whatsappMessageId?: string,
  metadata?: Record<string, unknown>,
  mediaUrl?: string
) {
  try {
    const { data: schema } = await supabase.rpc('get_user_tenant_schema');
    if (!schema) throw new Error('No tenant schema found');

    const { data: rpcResult, error } = await supabase.rpc('save_tenant_message', {
      tenant_schema: schema,
      conversation_id: conversationId,
      sender_type: senderType,
      sender_id: senderId,
      sender_uuid: senderUuid || null,
      content: content,
      message_type: messageType,
      external_id: whatsappMessageId || null,
      media_url: mediaUrl || null, // Pass the media URL if provided
      metadata: metadata || null
    });

    if (error) return { data: null, error };

    // Defensive handling: RPC can return different formats
    if (rpcResult) {
      console.log('[saveTenantMessage] 🔍 RPC Result type and value:', typeof rpcResult, rpcResult);
      
      let messageId: string;
      
      // Handle different return formats defensively
      if (typeof rpcResult === 'string') {
        // Direct UUID string
        messageId = rpcResult;
        console.log('[saveTenantMessage] ✅ Using direct UUID string');
      } else if (rpcResult && typeof rpcResult === 'object') {
        // Object with message_id property
        const resultObj = rpcResult as Record<string, unknown>;
        messageId = resultObj.message_id || resultObj.id || String(rpcResult);
        console.log('[saveTenantMessage] ✅ Extracted ID from object:', messageId);
      } else {
        console.error('[saveTenantMessage] ❌ Unexpected RPC result format:', rpcResult);
        return { data: null, error: new Error('Invalid message ID format from database') };
      }
      
      const message = {
        id: messageId, // Defensively extracted message ID
        conversation_id: conversationId,
        sender_type: senderType,
        sender_id: senderId,
        sender_uuid: senderUuid || null,
        content: content,
        message_type: messageType,
        external_id: whatsappMessageId || null,
        metadata: metadata || null,
        is_read: false,
        created_at: new Date().toISOString(), // Use current timestamp since RPC doesn't return it
        delivered_at: null,
        read_at: null,
        status: 'sending'
      };
      return { data: message, error: null };
    }

    return { data: null, error: new Error('No data returned from save operation') };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}

export async function markTenantMessagesAsRead(conversationId: string, userId: string) {
  try {
    const { data: schema } = await supabase.rpc('get_user_tenant_schema');
    if (!schema) throw new Error('No tenant schema found');

    const { data, error } = await supabase.rpc('mark_tenant_messages_as_read', {
      tenant_schema: schema,
      conversation_id_param: conversationId,
      user_id_param: userId
    });

    return { data, error };
  } catch (error) {
    return { data: null, error: error as Error };
  }
}